import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
  postHotWorkApproval,
  updateHotWorkApproval,
  getHotWorkPerformers,
  getResponsiblePersonsHWA,
  postHotWorkPeople,
  updateHotWorkPeople,
} from '../../../services/hotWorkApprovalService';
import { getLocation } from '../../../services/locationService';
import TimePicker from '../../../components/TimePicker/TimePicker';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';
import { formatDate } from '../../../utils/date';

interface HotWork {
  id: number;
  refNo: string;
  createdDate: string;
  workBy: string;
  location: string;
  status: string;
  statusColor: string;
  jobNumber: string;
  workToBePerformed?: string;
  natureOfJob?: string;
  personName?: string;
}

interface Props {
  editId?: number | null;
  initialData?: HotWork;
  editFullData?: any;
  onClose: () => void;
  onSuccess: (message: { title: string; message: string }) => void;
}

interface FormValues {
  workBy: 'employee' | 'contractor' | '';
  locationId: string;
  workToBePerformed: string;
  permitExpiryDate: string;
  permitExpiryTime: string;
  hotWorkPerformerId: string;
  fireWatchMonitorId: string;
}

interface FormErrors {
  workBy?: string;
  locationId?: string;
  workToBePerformed?: string;
  permitExpiryDate?: string;
  permitExpiryTime?: string;
  hotWorkPerformerId?: string;
  fireWatchMonitorId?: string;
}

interface Location {
  id: number;
  title: string;
}

interface Person {
  id: number;
  name: string;
  role?: string;
  avatar?: string;
}

const steps = [
  { title: 'Work Details', key: 'work-details' },
  { title: 'People Involved', key: 'people-involved' },
  { title: 'Preview & Add', key: 'preview' },
];

const AddHotWorkApprovalModal: React.FC<Props> = ({ editId, initialData, editFullData, onClose, onSuccess }) => {
  const isEditMode = Boolean(editId);
  const [currentStep, setCurrentStep] = useState(0);

  const [formValues, setFormValues] = useState<FormValues>({
    workBy: '',
    locationId: '',
    workToBePerformed: '',
    permitExpiryDate: '',
    permitExpiryTime: '',
    hotWorkPerformerId: '',
    fireWatchMonitorId: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const hasInitializedRef = useRef(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [performers, setPerformers] = useState<Person[]>([]);
  const [monitors, setMonitors] = useState<Person[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingPeople, setIsLoadingPeople] = useState(true);
  const [workDetailsSubmissionId, setWorkDetailsSubmissionId] = useState<number | null>(null);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoadingLocations(true);
        const data = await getLocation();
        setLocations(data);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // Fetch People for Step 2 (Hot Work Performer & Fire Watch)
  useEffect(() => {
    const fetchPeople = async () => {
      try {
        setIsLoadingPeople(true);

        const performersResponse = await getHotWorkPerformers();
        const monitorsResponse = await getResponsiblePersonsHWA();

        setPerformers(
          (performersResponse.data || performersResponse || []).map((person: any) => ({
            id: person.id,
            name: person.name,
            role: person.user_type?.title || '',
          })),
        );

        setMonitors(
          (monitorsResponse.data || monitorsResponse || []).map((person: any) => ({
            id: person.id,
            name: person.name,
            role: person.user_type?.title || '',
          })),
        );
      } catch (error) {
        console.error('Error fetching people:', error);
      } finally {
        setIsLoadingPeople(false);
      }
    };

    fetchPeople();
  }, []);

  useEffect(() => {
    if (!isEditMode || hasInitializedRef.current || hasUserEdited) return;

    const dataToUse = editFullData || initialData;
    if (!dataToUse) return;

    if (locations.length === 0 || performers.length === 0 || monitors.length === 0) return;

    const locationObj = locations.find((loc) => loc.id === dataToUse.location_id || loc.title === dataToUse.location);

    const performerId =
      dataToUse.people_involved?.hot_work_performer?.id || editFullData?.hot_work_performer_id || (initialData as any).hotWorkPerformerId;

    const monitorId =
      dataToUse.people_involved?.fire_watch?.id || editFullData?.fire_watch_monitor_id || (initialData as any).fireWatchMonitorId;

    setFormValues({
      workBy: ((dataToUse.work_by || initialData?.workBy) as 'employee' | 'contractor') || 'employee',
      locationId: locationObj ? String(locationObj.id) : '',
      workToBePerformed: dataToUse.nature_of_job || dataToUse.workToBePerformed || '',
      permitExpiryDate: dataToUse.permit_expiry_date || '',
      permitExpiryTime: dataToUse.permit_expiry_time || '',
      hotWorkPerformerId: performerId ? String(performerId) : '',
      fireWatchMonitorId: monitorId ? String(monitorId) : '',
    });

    hasInitializedRef.current = true;
  }, [isEditMode, editFullData, initialData, locations, performers, monitors, hasUserEdited]);

  const validateCurrentStep = (): boolean => {
    const newErrors: FormErrors = {};

    if (currentStep === 0) {
      if (!formValues.workBy) newErrors.workBy = 'Please select who is doing the work';
      if (!formValues.locationId) newErrors.locationId = 'Location is required';
      if (!formValues.workToBePerformed) newErrors.workToBePerformed = 'Work to be performed is required';
      if (!formValues.permitExpiryDate) newErrors.permitExpiryDate = 'Permit expiry date is required';
      if (!formValues.permitExpiryTime) newErrors.permitExpiryTime = 'Permit expiry time is required';
    } else if (currentStep === 1) {
      if (!formValues.hotWorkPerformerId) newErrors.hotWorkPerformerId = 'Hot Work Performer is required';
      if (!formValues.fireWatchMonitorId) newErrors.fireWatchMonitorId = 'Fire Watch / Safety Monitor is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWorkDetailsSubmission = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    try {
      const workDetailsPayload = {
        work_by: formValues.workBy,
        location_id: parseInt(formValues.locationId),
        nature_of_job: formValues.workToBePerformed,
        permit_expiry_date: formValues.permitExpiryDate,
        permit_expiry_time: formValues.permitExpiryTime,
      };

      const response = await postHotWorkApproval(workDetailsPayload);

      // Store the ID from the response
      const submissionId = response?.id || response?.data?.id;
      if (submissionId) {
        setWorkDetailsSubmissionId(submissionId);
        setCurrentStep((prev) => prev + 1);
      } else {
        onSuccess({
          title: 'Error',
          message: 'Failed to get submission ID from response.',
        });
      }
    } catch (error: any) {
      onSuccess({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to submit work details. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 0 && !isEditMode) {
      handleWorkDetailsSubmission();
    } else if (validateCurrentStep() && currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);
    try {
      if (isEditMode && editId) {
        await updateHotWorkApproval(editId, {
          work_by: formValues.workBy,
          location_id: parseInt(formValues.locationId),
          nature_of_job: formValues.workToBePerformed,
          permit_expiry_date: formValues.permitExpiryDate,
          permit_expiry_time: formValues.permitExpiryTime,
        });

        const peoplePayload = {
          hot_work_performer_id: parseInt(formValues.hotWorkPerformerId),
          fire_watch_id: parseInt(formValues.fireWatchMonitorId),
        };
        const peopleInvolvedId = editFullData?.people_involved?.id || (initialData as any)?.people_involved?.id;

        if (peopleInvolvedId) {
          await updateHotWorkPeople(peopleInvolvedId, peoplePayload);
        } else {
          await postHotWorkPeople({
            hot_work_approval_id: editId,
            ...peoplePayload,
          });
        }

        onSuccess({ title: 'Success', message: 'Hot Work Approval updated successfully.' });
      } else if (workDetailsSubmissionId) {
        const peoplePayload = {
          hot_work_approval_id: workDetailsSubmissionId,
          hot_work_performer_id: parseInt(formValues.hotWorkPerformerId),
          fire_watch_id: parseInt(formValues.fireWatchMonitorId),
        };
        await postHotWorkPeople(peoplePayload);
        onSuccess({ title: 'Success', message: 'Hot Work Approval added successfully.' });
      } else {
        onSuccess({
          title: 'Error',
          message: 'Work details submission ID not found. Please start over.',
        });
        return;
      }
      onClose();
    } catch (error: any) {
      onSuccess({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to save. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormValues, value: string) => {
    setHasUserEdited(true);
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Work Details
        return (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">
                Hot work being done by <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="workBy"
                    value="employee"
                    checked={formValues.workBy === 'employee'}
                    onChange={(e) => handleChange('workBy', e.target.value)}
                    className="cursor-pointer"
                  />
                  Employee
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="workBy"
                    value="contractor"
                    checked={formValues.workBy === 'contractor'}
                    onChange={(e) => handleChange('workBy', e.target.value)}
                    className="cursor-pointer"
                  />
                  Contractor
                </label>
              </div>
              {errors.workBy && <p className="text-red-500 text-xs mt-1">{errors.workBy}</p>}
            </div>

            <div></div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Location / Building and Floor <span className="text-red-500">*</span>
              </label>
              <select
                value={formValues.locationId}
                onChange={(e) => handleChange('locationId', e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
                disabled={isLoadingLocations}
              >
                <option value="">{isLoadingLocations ? 'Loading...' : 'Select Location'}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.title}
                  </option>
                ))}
              </select>
              {errors.locationId && <p className="text-red-500 text-xs mt-1">{errors.locationId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Work to be Performed <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Enter Work to be Performed"
                value={formValues.workToBePerformed}
                maxLength={250}
                onChange={(e) => handleChange('workToBePerformed', e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.workToBePerformed ? <p className="text-red-500 text-xs">{errors.workToBePerformed}</p> : <span />}
                <span className={`text-xs ${250 - formValues.workToBePerformed.length <= 10 ? 'text-red-500' : 'text-gray-400'}`}>
                  {250 - formValues.workToBePerformed.length} characters remaining
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Permit Expiry Date <span className="text-red-500">*</span>
              </label>
              <StandardDatePicker
                value={formValues.permitExpiryDate}
                onChange={(value) => handleChange('permitExpiryDate', value)}
                min={new Date().toISOString().split('T')[0]}
                error={Boolean(errors.permitExpiryDate)}
              />
              {errors.permitExpiryDate && <p className="text-red-500 text-xs mt-1">{errors.permitExpiryDate}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Permit Expiry Time <span className="text-red-500">*</span>
              </label>
              <TimePicker
                value={formValues.permitExpiryTime}
                onChange={(val) => handleChange('permitExpiryTime', val)}
                error={errors.permitExpiryTime}
              />
              {errors.permitExpiryTime && <p className="text-red-500 text-xs mt-1">{errors.permitExpiryTime}</p>}
            </div>
          </>
        );

      case 1: // People Involved — Updated as per your request
        return (
          <>
            <div>
              <label className="block text-sm font-medium mb-2">
                Hot Work Performer <span className="text-red-500">*</span>
              </label>
              <select
                value={formValues.hotWorkPerformerId}
                onChange={(e) => handleChange('hotWorkPerformerId', e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
                disabled={isLoadingPeople}
              >
                <option value="">{isLoadingPeople ? 'Loading...' : 'Select Hot Work Performer'}</option>
                {performers.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} {person.role ? `, ${person.role}` : ''}
                  </option>
                ))}
              </select>
              {errors.hotWorkPerformerId && <p className="text-red-500 text-xs mt-1">{errors.hotWorkPerformerId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Fire Watch / Safety Monitor <span className="text-red-500">*</span>
              </label>
              <select
                value={formValues.fireWatchMonitorId}
                onChange={(e) => handleChange('fireWatchMonitorId', e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-gray-100"
                disabled={isLoadingPeople}
              >
                <option value="">{isLoadingPeople ? 'Loading...' : 'Select Fire Watch / Safety Monitor'}</option>
                {monitors.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} {person.role ? `, ${person.role}` : ''}
                  </option>
                ))}
              </select>
              {errors.fireWatchMonitorId && <p className="text-red-500 text-xs mt-1">{errors.fireWatchMonitorId}</p>}
            </div>
          </>
        );

      case 2: // Preview & Add
        return (
          <div className="bg-gray-50 p-6 rounded-lg col-span-2">
            <h3 className="font-semibold mb-4 text-lg">Review Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Hot Work By</p>
                <p className="font-medium capitalize">{formValues.workBy}</p>
              </div>
              <div>
                <p className="text-gray-500">Location</p>
                <p className="font-medium">{locations.find((l) => String(l.id) === formValues.locationId)?.title || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Work to be Performed</p>
                <p className="font-medium">{formValues.workToBePerformed || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Hot Work Performer</p>
                <p className="font-medium">{performers.find((p) => String(p.id) === formValues.hotWorkPerformerId)?.name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Fire Watch / Safety Monitor</p>
                <p className="font-medium">{monitors.find((p) => String(p.id) === formValues.fireWatchMonitorId)?.name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Permit Expiry</p>
                <p className="font-medium">
                  {formatDate(formValues.permitExpiryDate)} at {formValues.permitExpiryTime}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg p-6 mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">{isEditMode ? 'Edit Hot Work Approval (HWA)' : 'Add Hot Work Approval (HWA)'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Stepper */}
        <div className="mb-8 px-12">
          <div className="flex items-start">
            {steps.map((step, index) => (
              <div key={step.key} className={`${index !== steps.length - 1 ? 'flex-1' : ''}`}>
                <div className="flex items-center">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${index <= currentStep ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {index !== steps.length - 1 && (
                    <div className={`flex-1 h-[1px] ${index < currentStep ? 'bg-green-500' : 'bg-gray-300'}`} />
                  )}
                </div>
                <div className={`mt-3 text-xs font-medium ${index === currentStep ? 'text-green-600' : 'text-gray-400'}`}>{step.title}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-2 gap-6 mb-8">{renderStepContent()}</div>

        {/* Footer Buttons */}
        <div className="border-t pt-6 flex justify-end gap-4">
          {/* Cancel button only on Step 1 and Step 3 */}
          {currentStep !== 1 && (
            <button onClick={onClose} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-50" disabled={isSubmitting}>
              Cancel
            </button>
          )}

          {currentStep > 0 && (
            <button onClick={handlePrevious} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-50" disabled={isSubmitting}>
              Previous
            </button>
          )}

          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Next'
              )}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditMode ? 'Updating...' : 'Adding...'}
                </>
              ) : isEditMode ? (
                'Update'
              ) : (
                'Add Approval'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddHotWorkApprovalModal;
